import { PhotographIcon } from "@heroicons/react/outline";
import { useCreate } from "@refinedev/core";
import { Loader } from "components/loader";
import { isEmpty } from "lodash";
import React from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { bytesForHuman, isInvalidImageSize } from "utills/helpers";

interface DropzoneProps {
  id?: string;
  value?: string[];
  [x: string]: any;
}
const BannerDropzone = React.forwardRef(
  ({ onChange, isMobile = false }: DropzoneProps, ref: any) => {
    const { mutate, isLoading: isLoading } = useCreate();

    const _uploadFiles = async (file: File) => {
      const formData = new FormData();
      formData.append("upload", file, `${file.name}`);
      return new Promise((resolve, reject) => {
        mutate(
          {
            dataProviderName: "externalUpload",
            resource: "/",
            values: formData,
          },
          {
            onSuccess: (data) => {
              const imageUrl = data.data?.result.img_url;
              resolve(imageUrl || "");
            },
          }
        );
      });
    };

    const {
      getRootProps: getRootkProps,
      getInputProps,
      open: openUpload,
    } = useDropzone({
      noClick: true,
      noKeyboard: true,
      multiple: true,
      accept: {
        "image/jpeg": [".jpeg", ".png", ".jpg"],
        // "application/pdf": ["pdf"],
      },
      validator: (file: File) => {
        const maximumSize = 3145728; // 3 MB
        if (isInvalidImageSize(file, maximumSize)) {
          return {
            code: "invalid-size",
            message: `Invalid file size.\n Maximum size: ${bytesForHuman(
              maximumSize
            )}.`,
          };
        }
        return null;
      },
      onDrop: async (acceptedFiles, rejectedFiles) => {
        console.log(acceptedFiles);
        if (acceptedFiles.length > 0 && isEmpty(rejectedFiles)) {
          for (let i = 0; i < acceptedFiles.length; i++) {
            const fileUrl: string = (await _uploadFiles(
              acceptedFiles[i]
            )) as string;
            if (!fileUrl) return;
            onChange(fileUrl);
          }
        }
      },
      onDropRejected: (errorFiles) => {
        if (!isEmpty(errorFiles)) {
          if (errorFiles[0].errors[0].code === "file-invalid-type") {
            return toast.error(
              `Only PNG, JPG, and JPEG file formats are supported`
            );
          }
          return toast.error(errorFiles[0].errors[0].message);
        }
      },
    });

    return (
      <section className="w-full h-full">
        <div {...getRootkProps({ className: "h-full" })}>
          <div
            className={`relative overflow-hidden flex flex-col justify-center items-center rounded-[8px]
                        bg-[#E9ECFF] text-center
                        ${isMobile ? "h-[136px]" : "h-[490px]"}
                      `}
          >
            {isLoading ? (
              <div
                className="opacity-100 bg-gray-900/40 duration-300 absolute inset-0
                            flex justify-center items-center rounded-lg"
              >
                <Loader size={40} />
              </div>
            ) : null}

            <div className="flex flex-col items-center gap-[8px]">
              <PhotographIcon
                className="h-12 w-12 text-[#4F5559] mx-auto"
                aria-hidden="true"
              />

              <input {...getInputProps()} />

              <p className="text-[14px] font-[400] text-[#4F5559]">
                Drag and drop file an Image
              </p>

              <button
                type="button"
                onClick={openUpload}
                className="text-[14px] font-[700] text-[#011BB6]"
              >
                Browse image
              </button>
            </div>
          </div>
          <p className="mt-1 text-sm text-[#868E95]">
            Maximum file size: 3MB <span className="mx-2">•</span> Supported
            image files: PNG, JPEG, JPG
          </p>
        </div>
      </section>
    );
  }
);

BannerDropzone.displayName = "BannerDropzone";

export default BannerDropzone;
